import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger } from "@/components/ui/select";
import { statuses, priorities, types } from "@/components/task/utils";
import { ExternalLink } from "lucide-react";
import { DataService } from "@/services/data";
import { toast } from "sonner";
import { TaskType } from "@/types/user";
import { useNavigate, useRevalidator } from "react-router-dom";
import { forwardRef } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useState } from "react";
import { DialogInner } from "./task-dialog-content";
import { DatePicker } from "@/components/ui/date-picker";
import { useUser } from "@/providers/user";
import { DateTime } from "luxon";

type TaskCardProps = {
    task: TaskType;
    is_manager: boolean;
    style?: {
        transform: string | undefined;
        opacity: number;
        zIndex: string | number;
        boxShadow: string | undefined;
    };
    className?: string;
};

export const TaskCard = forwardRef<HTMLDivElement, TaskCardProps>(({ task, is_manager, style, className, ...props }, ref) => {
    const revalidator = useRevalidator();
    const navigate = useNavigate();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { has_any_group } = useUser();
    const is_admin = has_any_group(["app_apprenticeship_admin"]);
    const [dueDate, setDueDate] = useState(task.due_date ? DateTime.fromISO(task.due_date) : undefined);
    const [updatingDueDate, setUpdatingDueDate] = useState(false);
    const task_type = types.find(type => type.value === task.type);
    const task_status = statuses.find(status => status.value === task.status);
    const task_priority = priorities.find(priority => priority.value === task.priority);

    return (
        <Dialog
            key={task.id}
            open={isDialogOpen}
            onOpenChange={open => {
                setIsDialogOpen(open);
            }}
        >
            <DialogTrigger asChild>
                <Card ref={ref} {...props} style={style} className={className}>
                    <CardHeader className="p-2">
                        <CardTitle className="flex flex-row items-center">
                            <span>{task_type?.icon}</span>
                            <span className="mx-1">-</span>
                            {task.title}
                        </CardTitle>
                        <CardDescription className="flex flex-row text-xs">
                            {task.reference !== null && task.reference_type === "apprenticeship:registration" && (
                                <span
                                    className="flex cursor-pointer flex-row items-center space-x-1 font-medium text-card-foreground hover:underline"
                                    onClick={event => {
                                        event.stopPropagation();
                                        event.preventDefault();
                                        let url = null;
                                        switch (task.type) {
                                            case "meeting":
                                                url = `/alternance/candidats/${task?.reference?.id}/rendez-vous`;
                                                break;
                                            case "email":
                                            case "note":
                                            case "call":
                                                url = `/alternance/candidats/${task?.reference?.id}/dossier`;
                                                break;
                                            default:
                                                break;
                                        }
                                        if (url) {
                                            if (event.metaKey || event.ctrlKey) {
                                                const win = window.open(url, "_blank");
                                                win?.focus();
                                            } else {
                                                navigate(url);
                                            }
                                        }
                                    }}
                                >
                                    <span>
                                        {task?.reference.first_name} {task?.reference.last_name}
                                    </span>
                                    <ExternalLink size={14} />
                                </span>
                            )}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-row p-2 text-sm">{task.description}</CardContent>
                    <CardFooter className="flex justify-between p-2">
                        <div className="flex flex-row space-x-1">
                            <Select
                                disabled={task.status_editable === false}
                                onValueChange={async value => {
                                    await DataService.client
                                        .url(`/users/tasks/${task.id}/`)
                                        .patch({ status: value })
                                        .json()
                                        .then(() => revalidator.revalidate())
                                        .catch(() => toast.error("Une erreur est survenue"));
                                }}
                            >
                                <SelectTrigger className="h-7 w-7 px-1 py-0">
                                    <div className="flex flex-row items-center space-x-1 text-xs">
                                        <div>{task_status?.icon}</div>
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {statuses.map((s, index) => (
                                            <SelectItem value={s.value} key={`${s.value}_${index}`}>
                                                <div className="flex flex-row items-center space-x-1 text-xs">
                                                    <div>{s.icon}</div>
                                                    <div>{s.label}</div>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>

                            <Select
                                disabled={task.priority_editable === false}
                                onValueChange={async value => {
                                    await DataService.client
                                        .url(`/users/tasks/${task.id}/`)
                                        .patch({ priority: value })
                                        .json()
                                        .then(() => revalidator.revalidate())
                                        .catch(() => toast.error("Une erreur est survenue"));
                                }}
                            >
                                <SelectTrigger className="h-7 w-7 px-1 py-0">
                                    <div className="flex flex-row items-center space-x-1 text-xs">
                                        <div>{task_priority?.icon}</div>
                                    </div>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectGroup>
                                        {priorities.map((p, index) => (
                                            <SelectItem value={p.value} key={`${p.value}_${index}`}>
                                                <div className="flex flex-row items-center space-x-1 text-xs">
                                                    <div>{p.icon}</div>
                                                    <div>{p.label}</div>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        </div>
                        {is_manager && <p className="text-xs">{task.assigned_to?.first_name}</p>}
                        {is_admin ? (
                            <div onClick={e => e.stopPropagation()}>
                                <DatePicker
                                    date={dueDate}
                                    setDate={async d => {
                                        setDueDate(d);
                                        setUpdatingDueDate(true);
                                        await DataService.client
                                            .url(`/users/tasks/${task.id}/`)
                                            .patch({ due_date: d ? d.toISODate() : null })
                                            .json()
                                            .then(() => revalidator.revalidate())
                                            .catch(() => toast.error("Erreur lors de la mise à jour de la date d'échéance"));
                                        setUpdatingDueDate(false);
                                    }}
                                    disabled={updatingDueDate}
                                    className="m-0 h-7 p-0 px-1 text-xs italic"
                                />
                            </div>
                        ) : (
                            <p className="text-xs italic">{task.due_date}</p>
                        )}
                    </CardFooter>
                </Card>
            </DialogTrigger>
            <DialogContent className="flex w-auto min-w-96 flex-1 flex-col">
                <DialogInner task={task} isDialogOpen={isDialogOpen} setIsDialogOpen={setIsDialogOpen} />
            </DialogContent>
        </Dialog>
    );
});
